import React, { createContext, useContext, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { cartAPI } from '../services/api';

interface CartContextType {
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  cartCount: number;
  secondsRemaining: number | null;
  isExpired: boolean;
  refetchCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch cart count and expiration info
  const { data: cartData, refetch } = useQuery(
    'cart-count',
    async () => {
      const response = await cartAPI.getCount();
      return response.data;
    },
    {
      refetchInterval: 30000, // Refetch every 30 seconds
      staleTime: 10000,
    }
  );

  const openCart = useCallback(() => {
    setIsCartOpen(true);
  }, []);

  const closeCart = useCallback(() => {
    setIsCartOpen(false);
  }, []);

  const toggleCart = useCallback(() => {
    setIsCartOpen((prev) => !prev);
  }, []);

  const refetchCart = useCallback(() => {
    refetch();
    queryClient.invalidateQueries('cart-sidebar');
  }, [refetch, queryClient]);

  return (
    <CartContext.Provider
      value={{
        isCartOpen,
        openCart,
        closeCart,
        toggleCart,
        cartCount: cartData?.count || 0,
        secondsRemaining: cartData?.seconds_remaining ?? null,
        isExpired: cartData?.is_expired || false,
        refetchCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
